import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { useUser } from "../../context/UserContext";
import { FormModal, Form, Field, FormSubmit } from "../form";
import { buildingSchema } from "../../services/projects/schemas";

const CreateBuildingModal = ({ isOpen, onClose, onCreate, editBuildingData, phaseId }) => {
    const { userInfo } = useUser();
    const [constructionPlans, setConstructionPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const isEdit = !!editBuildingData;

    // Load the available construction plans whenever the modal opens. We
    // also resolve the initial `constructionLinkedPlanId` for edit mode here
    // so the dropdown lands on the right value once options are present.
    useEffect(() => {
        if (!isOpen) return;
        const fetchConstructionPlans = async () => {
            setLoadingPlans(true);
            try {
                const resp = await axiosInstance.get("/construction/linked/plan", {
                    params: {
                        projectPhaseId: phaseId,
                        customerId: userInfo.loginIdentifierDetails.customer.customerId,
                    },
                });
                setConstructionPlans(resp || []);
            } catch (err) {
                console.error("Failed to fetch construction plans:", err);
                toast.error("Failed to fetch construction plans.");
            } finally {
                setLoadingPlans(false);
            }
        };
        fetchConstructionPlans();
    }, [isOpen, phaseId, userInfo?.loginIdentifierDetails?.customer?.customerId]);

    const planOptions = useMemo(
        () =>
            constructionPlans.map((p) => ({
                value: p.constructionLinkedPlanId,
                label: p.name,
            })),
        [constructionPlans],
    );

    // editBuildingData can have the linked plan id under either of two paths
    // (legacy flattened vs nested), so we try both before falling back to "".
    const linkedPlanId =
        editBuildingData?.constructionLinkedPlan?.constructionLinkedPlanId ??
        editBuildingData?.constructionLinkedPlanId ??
        "";

    const defaultValues = useMemo(
        () => ({
            buildingName: editBuildingData?.buildingName || "",
            numberOfFloors: editBuildingData?.numberOfFloors ?? "",
            constructionLinkedPlanId: linkedPlanId || "",
        }),
        [editBuildingData, linkedPlanId],
    );

    const handleSubmit = async (data) => {
        try {
            const body = {
                buildingName: data.buildingName,
                numberOfFloors: Number(data.numberOfFloors),
                constructionLinkedPlanId: Number(data.constructionLinkedPlanId),
            };
            const resp = isEdit
                ? await axiosInstance.put(
                      `/projects/buildings?buildingId=${editBuildingData.buildingId}`,
                      body,
                  )
                : await axiosInstance.post("/projects/buildings", body);
            onCreate?.(resp);
            toast.success(isEdit ? "Building updated successfully!" : "Building created successfully!");
            onClose();
        } catch (err) {
            console.error("API Error:", err);
            toast.error(
                err?.detail ||
                    err?.response?.data?.detail ||
                    err?.response?.data?.message ||
                    "Failed to create/update building",
            );
        }
    };

    return (
        <FormModal
            open={isOpen}
            onClose={onClose}
            title={isEdit ? "Edit Building" : "Add New Building"}
            size="sm"
        >
            {!loadingPlans && constructionPlans.length === 0 ? (
                <div className="text-center text-gray-600 p-4 bg-yellow-50 rounded-md">
                    <p className="mb-2 font-semibold">No Construction Plans Available</p>
                    <p className="text-sm">
                        Please create a construction plan first before adding a building.
                    </p>
                </div>
            ) : (
                <Form
                    key={editBuildingData?.buildingId ?? "new"}
                    schema={buildingSchema}
                    defaultValues={defaultValues}
                    onSubmit={handleSubmit}
                >
                    <Field
                        name="constructionLinkedPlanId"
                        label="Construction Plan"
                        type="select"
                        options={planOptions}
                        placeholder={loadingPlans ? "Loading plans..." : "Select a plan"}
                        required
                    />
                    <Field name="buildingName" label="Building Name" required />
                    <Field name="numberOfFloors" label="Number of Floors" type="number" min={1} required />
                    <FormSubmit
                        block
                        loadingLabel={isEdit ? "Updating..." : "Creating..."}
                    >
                        {isEdit ? "Update" : "Create"}
                    </FormSubmit>
                </Form>
            )}
        </FormModal>
    );
};

export default CreateBuildingModal;
